import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';

import { Button } from '../components/Button';
import { ApiError, login, loginTotp } from '../lib/api-client';
import { storeAccessToken } from '../lib/auth';
import './Login.css';

/**
 * 登入頁 — 版型對齊 docs/cms-screens/01-A1-login.png（雙欄品牌區 + 白卡表單）。
 *
 * 登入邏輯（密碼 → 視需要 2FA）維持不變；SSO 登入 / 忘記密碼 / 記住裝置 為
 * 設計稿視覺元素，功能未接入（SSO 待整合、忘記密碼頁未實作），故 SSO 鈕
 * disabled、記住裝置僅前端 state。
 */
export function LoginPage(): JSX.Element {
  const navigate = useNavigate();
  const [email, setEmail] = useState('admin@boat.local');
  const [password, setPassword] = useState('');
  const [totpCode, setTotpCode] = useState('');
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [rememberDevice, setRememberDevice] = useState(true);

  async function onSubmitCredentials(e: FormEvent): Promise<void> {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const res = await login({ email, password });
      if (res.access_token) {
        storeAccessToken(res.access_token);
        navigate('/', { replace: true });
        return;
      }
      if (res.two_factor_required && res.session_id) {
        setSessionId(res.session_id);
        return;
      }
      setError('未預期之回應');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : '登入失敗');
    } finally {
      setBusy(false);
    }
  }

  async function onSubmitTotp(e: FormEvent): Promise<void> {
    e.preventDefault();
    if (!sessionId) return;
    setError(null);
    setBusy(true);
    try {
      const r = await loginTotp({ session_id: sessionId, totp_code: totpCode });
      storeAccessToken(r.access_token);
      navigate('/', { replace: true });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : '2FA 驗證失敗');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="login-page">
      <aside className="login-brand">
        <div className="login-brand__top">
          <div className="login-brand__logo">⚓</div>
          <div>
            <div className="login-brand__name">Boat Ticket CMS</div>
            <div className="login-brand__sub">安平遊艇票務與導覽整合系統</div>
          </div>
        </div>
        <div className="login-brand__spacer" />
        <div className="login-brand__pill">系統可用 99.96%</div>
        <h2 className="login-brand__headline">統一管理｜售票、內容、設備、戰情</h2>
        <p className="login-brand__desc">
          網路、現場購票機、第三方分銷三通路一站維運。多語系內容釋出、Release Manifest
          簽章、設備異常即時告警。
        </p>
        <div className="login-brand__spacer" />
        <div className="login-brand__footer">v1.1 · © Anping Yacht Co. · 隸屬於戰情管理平台</div>
      </aside>

      <main className="login-form-pane">
        <div className="login-card">
          {!sessionId ? (
            <form onSubmit={onSubmitCredentials}>
              <h1 className="login-card__title">登入後台</h1>
              <p className="login-card__hint">使用您的公司帳號登入；首次登入請先綁定 2FA。</p>

              <div className="login-field">
                <div className="login-field__label">
                  <span>員工帳號 / Email</span>
                </div>
                <div className="login-input-wrap">
                  <span className="login-input-wrap__icon">👤</span>
                  <input
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                </div>
              </div>

              <div className="login-field">
                <div className="login-field__label">
                  <span>密碼</span>
                </div>
                <div className="login-input-wrap">
                  <span className="login-input-wrap__icon">🔒</span>
                  <input
                    type={showPassword ? 'text' : 'password'}
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                  />
                  <button
                    type="button"
                    className="login-input-wrap__eye"
                    aria-label={showPassword ? '隱藏密碼' : '顯示密碼'}
                    onClick={() => setShowPassword((v) => !v)}
                  >
                    {showPassword ? '🙈' : '👁'}
                  </button>
                </div>
              </div>

              <label className="login-checkbox">
                <input
                  type="checkbox"
                  checked={rememberDevice}
                  onChange={(e) => setRememberDevice(e.target.checked)}
                />
                記住此裝置 30 天（僅信任設備可用）
              </label>

              {error && <div className="error-banner">{error}</div>}

              <Button type="submit" variant="primary" className="login-btn-full" disabled={busy}>
                {busy ? '登入中…' : '登入並進行 2FA 驗證'}
              </Button>

              <div className="login-divider">或</div>

              <Button
                type="button"
                variant="secondary"
                className="login-btn-full"
                disabled
                title="SSO 待整合"
              >
                使用公司 SSO 登入
              </Button>

              <div className="login-notice">
                連續 5 次登入失敗將鎖定帳號 30 分鐘。SuperAdmin 必須啟用 2FA。
              </div>
            </form>
          ) : (
            <form onSubmit={onSubmitTotp}>
              <h1 className="login-card__title">2FA 驗證</h1>
              <p className="login-totp-hint">
                需要 TOTP 第二因素驗證（SuperAdmin 強制 2FA）。請輸入驗證器產生的 6 位數驗證碼。
              </p>

              <div className="login-field">
                <div className="login-field__label">
                  <span>6 位數驗證碼</span>
                </div>
                <input
                  className="login-totp-input"
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]{6}"
                  required
                  value={totpCode}
                  onChange={(e) => setTotpCode(e.target.value)}
                />
              </div>

              {error && <div className="error-banner">{error}</div>}

              <Button type="submit" variant="primary" className="login-btn-full" disabled={busy}>
                {busy ? '驗證中…' : '驗證並登入'}
              </Button>
              <Button
                type="button"
                variant="ghost"
                className="login-btn-full"
                onClick={() => setSessionId(null)}
              >
                返回
              </Button>
            </form>
          )}
        </div>
      </main>
    </div>
  );
}
